import { useCallback, useEffect, useMemo, useState } from 'react';
import { capitalize } from 'lodash';
import { v4 as uuid } from 'uuid';
import {
  type DescriptionFieldSetting,
  FieldSettingType,
} from '@timeedit/calendar-integration-api-contract';
import { type Column, TEInput } from '@timeedit/ui-components';
import { Button, Space, Table } from '@timeedit/ui-components/antd';
import DndWrapper, { DragHandle, Row, arrayMove } from './DndWrapper';
import ReservationValueInput from './ReservationValueInput';
import './ReservationValuePicker.scss';

export type ReservationValuePickerProps = {
  values: DescriptionFieldSetting[];
  onChange: (items: DescriptionFieldSetting[]) => void;
  showLabel?: boolean;
  addBtnLabel: string;
  removeBtnLabel: string;
};

const ReservationValuePicker = ({
  values,
  onChange,
  showLabel = true,
  addBtnLabel,
  removeBtnLabel,
}: ReservationValuePickerProps) => {
  const [localValues, setLocalValues] = useState<DescriptionFieldSetting[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  useEffect(() => {
    setLocalValues(values);
  }, [values]);

  const onRowAdd = useCallback(() => {
    onChange([
      ...localValues,
      {
        id: uuid(),
        text: '',
        type: FieldSettingType.freeText,
      },
    ]);
  }, [localValues, onChange]);

  const onRowChange = useCallback(
    (newValue: DescriptionFieldSetting) => {
      onChange(
        localValues.map((field) =>
          field.id === newValue.id ? newValue : field
        )
      );
    },
    [localValues, onChange]
  );

  const onRowsRemove = useCallback(
    (ids: string[]) => {
      onChange(localValues.filter((item) => !ids.includes(item.id)));
    },
    [localValues, onChange]
  );

  const columns = useMemo<Column<DescriptionFieldSetting>[]>(
    () => [
      {
        key: 'dragAndDrop',
        width: '35px',
        render: () => <DragHandle />,
      },
      ...(showLabel
        ? [
            {
              key: 'label',
              dataIndex: 'label',
              title: 'Label',
              width: '150px',
              render: (value, record) => (
                <TEInput.Input
                  value={value}
                  onChange={(val) => onRowChange({ ...record, label: val })}
                  size="small"
                />
              ),
            },
          ]
        : []),
      {
        key: 'type',
        dataIndex: 'type',
        title: 'Type',
        width: '150px',
        render: (value, record) => (
          <TEInput.Select
            value={value}
            options={pickerTypeOptions}
            onChange={(newValue) => {
              onRowChange({
                id: record.id,
                type: newValue,
                label: record.label,
                text: undefined,
              });
            }}
            size="small"
          />
        ),
      },
      {
        key: 'value',
        title: 'Value',
        width: '200px',
        render: (_, record) => (
          <ReservationValueInput
            value={record}
            onChange={(value) => {
              onRowChange(value);
            }}
          />
        ),
      },
    ],
    [onRowChange, showLabel]
  );

  const onDragEnd = useCallback(
    (sourceIndex: number, targetIndex: number) => {
      setLocalValues((prevState) => {
        const newList = arrayMove(prevState, sourceIndex, targetIndex);
        onChange(newList);
        return newList;
      });
    },
    [onChange]
  );

  return (
    <DndWrapper onDragEnd={onDragEnd} items={localValues.map((i) => i.id)}>
      <div className="reservation-value-picker">
        <Table
          rowKey={'id'}
          columns={columns}
          dataSource={localValues}
          rowSelection={{
            onChange: (selectedRowKeys) => {
              setSelectedIds(selectedRowKeys as string[]);
            },
          }}
          footer={() => (
            <Space direction="horizontal">
              <Button size="small" onClick={onRowAdd} type="primary">
                {addBtnLabel}
              </Button>
              <Button
                size="small"
                onClick={() => {
                  onRowsRemove(selectedIds);
                  setSelectedIds([]);
                }}
                disabled={selectedIds.length === 0}
              >
                {removeBtnLabel}
              </Button>
            </Space>
          )}
          components={{ body: { row: Row } }}
          pagination={false}
        />
      </div>
    </DndWrapper>
  );
};

export default ReservationValuePicker;

const pickerTypeOptions = Object.values(FieldSettingType).map((type) => ({
  value: type,
  label: capitalize(type.replaceAll('_', ' ')),
}));
